import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { XIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

interface LoginSuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginSuccessDialog: React.FC<LoginSuccessDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { setJustLoggedIn } = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);

  const handleGoToGoogleForm = useCallback(async () => {
    try {
      setIsNavigating(true);

      // Clear the flag first
      setJustLoggedIn(false);

      // Close the dialog
      onClose();

      // Open Google form in new tab (currently placeholder)
      window.open("https://google.com", "_blank");

      // Wait a moment for dialog to close
      await new Promise(resolve => setTimeout(resolve, 200));

      // Navigate to dashboard
      try {
        navigate("/dashboard");
      } catch (navError) {
        console.error(
          "❌ LoginSuccessDialog: Direct navigation failed, trying window.location:",
          navError
        );
        window.location.href = "/dashboard";
      }
    } catch (error) {
      console.error(
        "❌ LoginSuccessDialog: Error in Google form navigation:",
        error
      );
      // Final fallback
      window.location.href = "/dashboard";
    } finally {
      setIsNavigating(false);
    }
  }, [setJustLoggedIn, onClose, navigate]);

  const handleCloseToDashboard = useCallback(async () => {
    try {
      setIsNavigating(true);

      // Clear the flag first
      setJustLoggedIn(false);

      // Close the dialog first
      onClose();

      // Wait a moment for dialog to close
      await new Promise(resolve => setTimeout(resolve, 200));

      // Navigate to dashboard - use direct navigation first
      try {
        navigate("/dashboard");
      } catch (navError) {
        console.error(
          "❌ LoginSuccessDialog: Direct navigation failed, trying window.location:",
          navError
        );
        window.location.href = "/dashboard";
      }
    } catch (error) {
      console.error(
        "❌ LoginSuccessDialog: Error in close to dashboard navigation:",
        error
      );
      // Final fallback
      window.location.href = "/dashboard";
    } finally {
      setIsNavigating(false);
    }
  }, [setJustLoggedIn, onClose, navigate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-3xl p-8 max-w-md w-full relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180.69deg, #E9D8FA 0.59%, #D1DFFF 24.19%, #EFF3FB 64.64%)",
        }}
      >
        {/* Close button - goes to dashboard */}
        <button
          onClick={handleCloseToDashboard}
          disabled={isNavigating}
          className={`absolute top-6 right-6 transition-colors z-20 ${
            isNavigating
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1"
          }`}
          aria-label="Close and go to dashboard"
          title="Close and go to dashboard"
        >
          {isNavigating ? (
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          ) : (
            <XIcon className="w-6 h-6" />
          )}
        </button>

        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Left side decorations */}
          <div className="absolute top-8 left-8 w-4 h-2 bg-pink-300 rounded-full transform rotate-45"></div>
          <div className="absolute top-12 left-12 w-3 h-1 bg-yellow-300 rounded-full transform -rotate-12"></div>
          <div className="absolute top-20 left-6 w-2 h-4 bg-orange-300 rounded-full transform rotate-75"></div>
          <div className="absolute top-24 left-16 w-3 h-2 bg-purple-300 rounded-full transform -rotate-30"></div>
          <div className="absolute top-32 left-10 w-2 h-3 bg-blue-300 rounded-full transform rotate-60"></div>

          {/* Right side decorations */}
          <div className="absolute top-8 right-8 w-4 h-2 bg-pink-300 rounded-full transform -rotate-45"></div>
          <div className="absolute top-12 right-12 w-3 h-1 bg-yellow-300 rounded-full transform rotate-12"></div>
          <div className="absolute top-20 right-6 w-2 h-4 bg-orange-300 rounded-full transform -rotate-75"></div>
          <div className="absolute top-24 right-16 w-3 h-2 bg-purple-300 rounded-full transform rotate-30"></div>
          <div className="absolute top-32 right-10 w-2 h-3 bg-purple-300 rounded-full transform -rotate-60"></div>

          {/* Additional scattered decorations */}
          <div className="absolute top-16 left-1/4 w-2 h-2 bg-yellow-300 rounded-full transform rotate-45"></div>
          <div className="absolute top-28 right-1/4 w-3 h-1 bg-pink-300 rounded-full transform -rotate-30"></div>
          <div className="absolute top-36 left-1/3 w-1 h-3 bg-orange-300 rounded-full transform rotate-90"></div>
          <div className="absolute top-40 right-1/3 w-2 h-2 bg-yellow-300 rounded-full transform -rotate-45"></div>
        </div>

        {/* Success icon */}
        <div className="flex justify-center mb-6 relative z-10">
          <div className="w-24 h-24 flex items-center justify-center">
            <img
              src="/feedback.png"
              alt="Login Success Feedback"
              className="w-20 h-20 object-contain"
            />
          </div>
        </div>

        {/* Content */}
        <div className="text-center relative z-10">
          <h2
            className="text-2xl font-bold mb-4"
            style={{
              background: "linear-gradient(90deg, #6A2CFE 0%, #010FFE 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Feedback Anda, Dampak Besar untuk Guru
          </h2>
          <p className="text-[#373e46] mb-8 leading-relaxed">
            Semakin banyak feedback, semakin baik kami meningkatkan pengalaman
            pengguna
          </p>

          {/* Google Form button */}
          <Button
            onClick={handleGoToGoogleForm}
            disabled={isNavigating}
            className={`w-full h-12 font-semibold rounded-xl transition-all duration-300 ${
              isNavigating
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
            }`}
          >
            {isNavigating ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Memproses...
              </div>
            ) : (
              "Isi Sebentar"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
